/**
 * QUÉ ESTADO MOSTRAR CUANDO LLEGA UN AVISO DEL TRABAJO.
 *
 * El worker manda avisos por WebSocket mientras arma o cotiza una hoja. Cada aviso trae
 * dos cosas parecidas que NO son lo mismo:
 *
 *   • `stage`  — en qué paso va el trabajo (leyendo el documento, buscando precios…).
 *   • `status` — con qué estado queda la HOJA.
 *
 * En casi todos los avisos coinciden con lo que hay en la base. En uno no: al terminar de
 * **armar la hoja** (leer el documento), el worker deja la hoja en `draft` —"lista para
 * revisar", que es su estado de verdad— y acto seguido publica el aviso con
 * `status: 'done'`, que ahí significa "terminó ESTE PASO", no "la hoja quedó en done".
 * La hoja nunca queda en `done` por armarse.
 *
 * Si la pantalla se cree ese `done`, durante un tercio de segundo dibuja un estado que el
 * sistema no tiene, hasta que llega la respuesta del servidor con la verdad. Y ese tercio
 * de segundo SE VE: los avisos que van arriba del listado aparecen y desaparecen, y el
 * árbol de conceptos sube y vuelve a bajar solo.
 *
 * MEDIDO el 2026-08-27 (hoja de 2.838 renglones de ABEL MATA, usuario quieto, sin panel
 * abierto, sin tocar nada, empujándole al navegador los mismos avisos del worker):
 *   • llega el aviso → se va el panel "Armando la hoja de presupuesto…" (98 px) y vuelve
 *     la fila de filtros (30 px): **el árbol sube 55,6 px**, uno o dos renglones;
 *   • 328 ms después llega la respuesta del servidor con `draft` → aparece el aviso "La
 *     hoja está armada, todavía sin precios" (72 px, que solo se muestra en `draft`):
 *     **el árbol baja 71,6 px**.
 * Sube y se baja. Es el parpadeo que reportó Sebastián ("luego de generar la hp, sin
 * hacer nada, se corre un poquito el árbol"). El listado virtualizado no tuvo nada que
 * ver: el alto total se movió 31 px en todo el episodio y no hubo un solo scroll
 * programático. Con esta regla el movimiento pasó a ser **uno solo, de 16 px**, en el
 * mismo cuadro en que llega el dato de verdad.
 *
 * La regla, entonces: **al terminar de armarse, el estado lo dice el servidor.** Para
 * todo lo demás el aviso manda, porque ahí el worker escribió en la base exactamente lo
 * mismo que publica (el cierre de la cotización, la pausa, el fallo y la detención).
 */

/** Lo mínimo que se mira de un aviso `quote:progress`. */
export interface AvisoDeTrabajo {
  stage: string;
  status?: string;
}

/** ¿Este aviso anuncia que la hoja TERMINÓ DE ARMARSE (se leyó el documento)? */
export function esElFinDelArmado(aviso: AvisoDeTrabajo): boolean {
  return aviso.stage === 'extract' && aviso.status === 'done';
}

/**
 * Estado que la pantalla debe mostrar al recibir un aviso.
 *
 * @param estadoActual el que ya está en pantalla
 * @param aviso        lo que acaba de llegar por WebSocket
 * @returns el estado a mostrar; `undefined` si no hay nada que mostrar todavía
 */
export function estadoDeLaHojaTrasElAviso(
  estadoActual: string | undefined,
  aviso: AvisoDeTrabajo,
): string | undefined {
  // Fin del armado: no se le cree al aviso. Se deja lo que hay hasta que conteste el
  // servidor, así el panel de avance y el aviso de "hoja armada" se relevan en el mismo
  // cuadro en vez de dejar un hueco en el medio.
  if (esElFinDelArmado(aviso)) return estadoActual;
  return aviso.status ?? estadoActual;
}
